import {
  ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  ViewMode,
  useSlidePanel,
} from '@gripp/shared-ui';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { Issue } from '../../issues/components/issue';
import ActivityEditLabels from './activity-edit-label';
import { ActivityEditColumn } from './activityEditColumn';

const ACTIVITY_EDIT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activity-edit.tsx';
const ACTIVITY_EDIT_COLUMN_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activityEditColumn.tsx';

const useActivityEditState = () => {
  const { issueId = '', assetId } = useParams();
  const navigate = useNavigate();
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }> | undefined
  >(undefined);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  const editBackButtonGuard = useCallback((): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false),
      );
    });
  }, []);

  const backButtonGuard = useCallback((): Promise<boolean> => {
    if (isLabelsPanelOpen) {
      setIsLabelsPanelOpen(false);
      return Promise.resolve(false);
    }
    return editBackButtonGuard();
  }, [editBackButtonGuard, isLabelsPanelOpen]);

  return {
    assetId,
    backButtonGuard,
    backGuardRef,
    editBackButtonGuard,
    isLabelsPanelOpen,
    issueId,
    navigate,
    selectedLabels,
    setIsLabelsPanelOpen,
    setSelectedLabels,
  };
};

type ActivityEditState = ReturnType<typeof useActivityEditState>;

const useIssueDetailColumn = ({
  assetId,
  backButtonGuard,
  issueId,
  showAssetLink,
}: Pick<ActivityEditState, 'assetId' | 'backButtonGuard' | 'issueId'> & {
  showAssetLink: boolean;
}) =>
  useMemo<ColumnData>(
    () => ({
      title: 'activities.detail.title',
      showHeader: false,
      showBackButton: false,
      disableScroll: true,
      mainComponent: (
        <AssetRouteProvider
          value={{
            basePath: `/activities/${issueId}`,
            listPath: '/activities',
            isWorkspaceMode: false,
            issueId,
          }}
        >
          <Issue
            isComingFromActivities
            onBackPressGuard={backButtonGuard}
            showAssetLink={showAssetLink && !assetId}
          />
        </AssetRouteProvider>
      ),
    }),
    [assetId, backButtonGuard, issueId, showAssetLink],
  );

const useEditColumn = ({
  backGuardRef,
  editBackButtonGuard,
  isLabelsPanelOpen,
  issueId,
  navigate,
  selectedLabels,
  setIsLabelsPanelOpen,
  setSelectedLabels,
  standalone,
}: Pick<
  ActivityEditState,
  | 'backGuardRef'
  | 'editBackButtonGuard'
  | 'isLabelsPanelOpen'
  | 'issueId'
  | 'navigate'
  | 'selectedLabels'
  | 'setIsLabelsPanelOpen'
  | 'setSelectedLabels'
> & {
  standalone: boolean;
}) =>
  useMemo<ColumnData>(
    () => ({
      title: 'issue.menu.edit',
      showBackButton: true,
      ...(standalone
        ? {
            backButtonTransition: SlideAction.PREV,
            backButtonTransitionBehavior: 'concurrent' as const,
            ...(isLabelsPanelOpen
              ? { backButtonTransitionExitColumn: 'both' as const }
              : {}),
          }
        : {}),
      backButtonGuard: editBackButtonGuard,
      onBackButtonPress: () =>
        navigate('..', {
          relative: 'path',
          state: standalone
            ? {
                skipAutoSelect: true,
                slideLayoutInitialTransition: SlideAction.PREV,
              }
            : { skipAutoSelect: true },
        }),
      mainComponent: (
        <ActivityEditColumn
          selectedLabels={selectedLabels}
          onLabelsChange={setSelectedLabels}
          backGuardRef={backGuardRef}
          onOpenLabelsPanel={() => setIsLabelsPanelOpen(true)}
        />
      ),
      disableScroll: true,
      debug: {
        id: 'activity-edit:edit',
        titleKey: 'issue.menu.edit',
        component: 'ActivityEditColumn',
        componentSource: ACTIVITY_EDIT_COLUMN_SOURCE,
        columnSource: ACTIVITY_EDIT_SOURCE,
        ownerComponent: standalone ? 'ActivityEdit' : 'ActivityEditPanel',
        ownerSource: ACTIVITY_EDIT_SOURCE,
        routePanelKey: 'activity-edit',
        routePattern: '/activities/:issueId/edit',
        routeSource: 'gripp-workspace/apps/gripp-web/src/activity/routes.tsx',
        source: {
          owner: {
            file: ACTIVITY_EDIT_SOURCE,
            symbol: standalone ? 'ActivityEdit' : 'ActivityEditPanel',
          },
          column: {
            file: ACTIVITY_EDIT_SOURCE,
            symbol: 'useEditColumn',
          },
          component: {
            file: ACTIVITY_EDIT_COLUMN_SOURCE,
            symbol: 'ActivityEditColumn',
          },
          route: {
            file: 'gripp-workspace/apps/gripp-web/src/activity/routes.tsx',
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'activity-edit', id: issueId },
      },
    }),
    [
      backGuardRef,
      editBackButtonGuard,
      isLabelsPanelOpen,
      issueId,
      navigate,
      selectedLabels,
      setIsLabelsPanelOpen,
      setSelectedLabels,
      standalone,
    ],
  );

const useLabelsColumn = ({
  isLabelsPanelOpen,
  issueId,
  selectedLabels,
  setIsLabelsPanelOpen,
  setSelectedLabels,
}: Pick<
  ActivityEditState,
  | 'isLabelsPanelOpen'
  | 'issueId'
  | 'selectedLabels'
  | 'setIsLabelsPanelOpen'
  | 'setSelectedLabels'
>) =>
  useMemo<ColumnData | undefined>(
    () =>
      isLabelsPanelOpen
        ? {
            title: 'issue.new.labels',
            mainComponent: (
              <ActivityEditLabels
                issueId={issueId}
                selectedLabels={selectedLabels ?? []}
                onLabelsChange={setSelectedLabels}
              />
            ),
            showBackButton: true,
            onBackButtonPress: () => setIsLabelsPanelOpen(false),
            disableScroll: true,
          }
        : undefined,
    [
      isLabelsPanelOpen,
      issueId,
      selectedLabels,
      setIsLabelsPanelOpen,
      setSelectedLabels,
    ],
  );

export const ActivityEditPanel = () => {
  const editState = useActivityEditState();
  const detailColumn = useIssueDetailColumn({
    assetId: editState.assetId,
    backButtonGuard: editState.backButtonGuard,
    issueId: editState.issueId,
    showAssetLink: false,
  });
  const editColumn = useEditColumn({ ...editState, standalone: false });
  const labelsColumn = useLabelsColumn(editState);

  useSlidePanel('right', detailColumn, [detailColumn]);
  useSlidePanel('third', editColumn, [editColumn]);
  useSlidePanel('fourth', labelsColumn ?? null, [labelsColumn]);

  return null;
};

export const ActivityEdit = () => {
  const editState = useActivityEditState();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const leftColumn = useIssueDetailColumn({
    assetId: editState.assetId,
    backButtonGuard: editState.backButtonGuard,
    issueId: editState.issueId,
    showAssetLink: true,
  });
  const rightColumn = useEditColumn({ ...editState, standalone: true });
  const thirdColumn = useLabelsColumn(editState);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
